import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { SimpleSelect } from '@/components/shared/simple-select';
import { Button } from '@/components/ui/button';
import type { Page } from '@/lib/types';

const PAGE_SIZES = [25, 50, 100];
const DEFAULT_PAGE_SIZE = 25;

/**
 * Page-by-page browsing over the API's keyset cursors. The server never skips rows (CLAUDE.md), so there is
 * no "jump to page N": we remember the cursor that opened each page we've been to and step back through them.
 * Changing the filters (the query key) or the page size starts again from page 1.
 */
export function useCursorPages<T>({ queryKey, fetchPage, refetchInterval }: {
  queryKey: readonly unknown[];
  fetchPage(before: string | undefined, limit: number): Promise<Page<T>>;
  refetchInterval?: number;
}) {
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [cursors, setCursors] = useState<(string | undefined)[]>([undefined]);
  const resetKey = JSON.stringify([queryKey, pageSize]);
  const [seenKey, setSeenKey] = useState(resetKey);
  if (seenKey !== resetKey) { setSeenKey(resetKey); setCursors([undefined]); }

  const page = cursors.length - 1;
  const cursor = cursors[page];
  const q = useQuery({
    queryKey: [...queryKey, 'page', pageSize, cursor ?? null],
    queryFn: () => fetchPage(cursor, pageSize),
    placeholderData: keepPreviousData,
    refetchInterval,
  });
  // While the next page loads, q.data is still the previous page's: don't let its cursor be pushed twice.
  const next = !q.isPlaceholderData ? q.data?.next ?? null : null;

  return {
    query: q,
    rows: q.data?.data ?? [],
    page, pageSize, setPageSize,
    hasPrev: page > 0,
    hasNext: !!next,
    goPrev: () => setCursors((c) => (c.length > 1 ? c.slice(0, -1) : c)),
    goNext: () => { if (next) setCursors((c) => [...c, next]); },
  };
}

type CursorPages = Pick<ReturnType<typeof useCursorPages>, 'rows' | 'page' | 'pageSize' | 'setPageSize' | 'hasPrev' | 'hasNext' | 'goPrev' | 'goNext'> & {
  query: { isFetching: boolean; isPlaceholderData: boolean };
};

/** Table footer: rows per page, the range shown, and Previous / Next. */
export function Pagination({ p, noun = 'rows' }: { p: CursorPages; noun?: string }) {
  if (p.rows.length === 0 && !p.hasPrev) return null;
  const from = p.page * p.pageSize + 1;
  const to = from + p.rows.length - 1;
  const busy = p.query.isPlaceholderData;
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t px-5 py-3 text-sm">
      <div className="flex items-center gap-2 text-muted-foreground">
        <span className="whitespace-nowrap">Rows per page</span>
        <SimpleSelect
          className="h-8 w-20" value={String(p.pageSize)} onValueChange={(v) => p.setPageSize(Number(v))}
          options={PAGE_SIZES.map((n) => ({ value: String(n), label: String(n) }))}
        />
      </div>
      <div className="flex items-center gap-4">
        <span className="text-muted-foreground tabular-nums">
          {p.rows.length ? <>{from.toLocaleString()}–{to.toLocaleString()} {noun}</> : 'No more results'} · Page {p.page + 1}
        </span>
        <div className="flex items-center gap-1.5">
          <Button variant="outline" size="icon-sm" aria-label="Previous page" disabled={!p.hasPrev || busy} onClick={p.goPrev}><ChevronLeft /></Button>
          <Button variant="outline" size="icon-sm" aria-label="Next page" disabled={!p.hasNext || busy} onClick={p.goNext}><ChevronRight /></Button>
        </div>
      </div>
    </div>
  );
}
